/**
 * Sync worker — drains the offline queue against the real API.
 *
 * Run pattern:
 *   - Boot: call `startSyncWorker()` once from main.tsx. It installs a
 *     30-second interval and an `online` event listener.
 *   - Manual: `syncNow()` is safe to call anywhere (banner "Sync now" button,
 *     tests, etc). It's guarded against re-entrancy so parallel triggers
 *     collapse into one flush.
 *
 * Error policy:
 *   - 2xx → remove from queue. For POSTs, if the response body carries an
 *     `id`, remember the tempId → serverId mapping so subsequent writes get
 *     their URLs rewritten.
 *   - 4xx (400/409/422) → push to dead-letter list, remove from queue. These
 *     will never succeed on retry so the user needs to review them.
 *   - 5xx / network → leave in queue, bump attempt count, schedule next
 *     attempt with exponential backoff (max 5min).
 */
import type { AxiosError } from 'axios';
import { api } from '@/shared/api/client';
import {
  getTempIdMap,
  list,
  pushDeadLetter,
  remove,
  setTempIdMapping,
  update,
} from './offlineQueue';

const SYNC_INTERVAL_MS = 30_000;
const MAX_BACKOFF_MS = 5 * 60_000;

/** Compute the backoff delay for a given attempt count (attempts=1 → 2s). */
function backoffFor(attempts: number): number {
  const base = 2_000;
  const raw = base * Math.pow(2, Math.max(0, attempts - 1));
  return Math.min(raw, MAX_BACKOFF_MS);
}

/**
 * Rewrite `…/tempId` at the end of a URL to `…/serverId` when we have a
 * mapping. Preserves any trailing sub-path (`/leads/tmp_xxx/convert`).
 */
function rewriteUrl(url: string, map: Record<string, string | number>): string {
  const marker = '/tmp_';
  const idx = url.indexOf(marker);
  if (idx < 0) return url;
  const tail = url.slice(idx + 1);
  const [segment, ...rest] = tail.split('/');
  const mapped = map[segment];
  if (mapped == null) return url;
  const rebuilt = `${url.slice(0, idx + 1)}${mapped}`;
  return rest.length ? `${rebuilt}/${rest.join('/')}` : rebuilt;
}

let flushing = false;

export async function syncNow(): Promise<void> {
  if (flushing) return;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
  flushing = true;
  try {
    const queue = await list();
    if (queue.length === 0) return;

    const tempMap = await getTempIdMap();
    const now = Date.now();

    for (const item of queue) {
      if (item.nextAttemptAt && item.nextAttemptAt > now) continue;

      const url = rewriteUrl(item.url, tempMap);
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const res = await api.request<any>({
          url,
          method: item.method,
          data: item.body,
        });

        // POST that created a record? Remember its server-assigned id so
        // any queued follow-up writes can be rewritten.
        if (item.method === 'POST' && item.tempId) {
          const data = res.data;
          const inner = data && typeof data === 'object' && 'data' in data ? data.data : data;
          const serverId = inner && typeof inner === 'object' ? inner.id : undefined;
          if (serverId != null) {
            await setTempIdMapping(item.tempId, serverId);
            tempMap[item.tempId] = serverId;
          }
        }

        await remove(item.id);
        notifyListeners();
      } catch (err) {
        const axErr = err as AxiosError;
        const status = axErr.response?.status ?? 0;
        if (status >= 400 && status < 500) {
          await pushDeadLetter({
            ...item,
            failedAt: Date.now(),
            status,
            reason:
              (axErr.response?.data as { message?: string } | undefined)?.message ??
              axErr.message,
          });
          await remove(item.id);
          notifyListeners();
        } else {
          const attempts = (item.attempts ?? 0) + 1;
          await update({
            ...item,
            attempts,
            nextAttemptAt: Date.now() + backoffFor(attempts),
          });
          // 5xx / network — stop draining; the interval will retry.
          break;
        }
      }
    }
  } finally {
    flushing = false;
  }
}

/* -------------------------------------------------------------------------- */
/* Change notifications — the OfflineBanner subscribes to keep its count in  */
/* sync without polling.                                                     */
/* -------------------------------------------------------------------------- */
type Listener = () => void;
const listeners = new Set<Listener>();

export function onQueueChange(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function notifyListeners(): void {
  for (const l of listeners) {
    try {
      l();
    } catch {
      /* consumer errors shouldn't break other subscribers */
    }
  }
}

let started = false;
let intervalId: ReturnType<typeof setInterval> | null = null;

export function startSyncWorker(): void {
  if (started || typeof window === 'undefined') return;
  started = true;
  window.addEventListener('online', () => {
    void syncNow();
  });
  intervalId = setInterval(() => {
    void syncNow();
  }, SYNC_INTERVAL_MS);
  // Kick once at boot in case we came back online while the tab was closed.
  if (navigator.onLine) void syncNow();
}

export function stopSyncWorker(): void {
  if (intervalId != null) clearInterval(intervalId);
  intervalId = null;
  started = false;
}
